import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { roomsApi } from '../../../services/api';

interface DateTimePickerProps {
  roomId: number | null;
  selectedDate: string;
  selectedTime: string;
  onDateChange: (date: string) => void;
  onTimeChange: (time: string) => void;
  error?: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const SectionTitle = styled.h4`
  margin: 0;
  font-size: ${props => props.theme.typography.fontSize.md};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
`;

const DateInput = styled.input<{ $hasError?: boolean }>`
  min-height: 44px;
  padding: 12px 16px;
  border: 2px solid ${props => props.$hasError ? props.theme.colors.error : 'rgba(255, 255, 255, 0.2)'};
  background: ${props => props.theme.colors.background.tertiary};
  color: ${props => props.theme.colors.text.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.md};
  transition: all ${props => props.theme.transitions.normal};
  
  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? props.theme.colors.error : props.theme.colors.primary.main};
    box-shadow: 0 0 0 3px ${props => props.$hasError ? 'rgba(255, 59, 48, 0.1)' : 'rgba(0, 122, 255, 0.1)'};
  }
  
  /* Style the date picker */
  &::-webkit-calendar-picker-indicator {
    filter: invert(1);
    cursor: pointer;
  }
`;

const TimeSlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: ${props => props.theme.spacing.sm};
  
  @media (max-width: 767px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const TimeSlotButton = styled.button<{ $isSelected?: boolean; $isDisabled?: boolean }>`
  min-height: 44px;
  padding: 8px 12px;
  border: 2px solid ${props => {
    if (props.$isSelected) return props.theme.colors.primary.main;
    if (props.$isDisabled) return 'rgba(255, 255, 255, 0.1)';
    return 'rgba(255, 255, 255, 0.2)';
  }};
  background: ${props => {
    if (props.$isSelected) return props.theme.colors.primary.main;
    if (props.$isDisabled) return 'rgba(255, 255, 255, 0.05)';
    return props.theme.colors.background.tertiary;
  }};
  color: ${props => {
    if (props.$isSelected) return 'white';
    if (props.$isDisabled) return props.theme.colors.text.tertiary;
    return props.theme.colors.text.primary;
  }};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  transition: all ${props => props.theme.transitions.normal};
  
  &:hover:not(:disabled) {
    background: ${props => props.$isSelected ? props.theme.colors.primary.main : 'rgba(255, 255, 255, 0.1)'};
    border-color: ${props => props.$isSelected ? props.theme.colors.primary.main : 'rgba(255, 255, 255, 0.3)'};
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
`;

const LoadingMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  font-size: ${props => props.theme.typography.fontSize.sm};
  text-align: center;
  padding: ${props => props.theme.spacing.md};
  background: rgba(255, 59, 48, 0.1);
  border-radius: ${props => props.theme.borderRadius.md};
`;

const NoSlotsMessage = styled.div`
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.fontSize.sm};
  text-align: center;
  padding: ${props => props.theme.spacing.lg};
  background: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.md};
`;

// No more hardcoded schedules - only use data from API

// Helper function to format time for display
const formatTimeForDisplay = (time: string): string => {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minutes} ${ampm}`;
};

// Helper function to get minimum date (today)
const getMinDate = (): string => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  roomId,
  selectedDate,
  selectedTime,
  onDateChange,
  onTimeChange,
  error,
}) => {
  const [allSlots, setAllSlots] = useState<{time: string, status: string}[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const onTimeChangeRef = useRef(onTimeChange);

  // Fetch all slots when date or room changes
  useEffect(() => {
    const fetchAllSlots = async () => {
      if (!roomId || !selectedDate) {
        setAllSlots([]);
        return;
      }

      setIsLoading(true);
      setFetchError(null);

      try {
        const slots = await roomsApi.getAllSlots(roomId, selectedDate);
        setAllSlots(slots);
        
        // Clear selected time if it's no longer available (only if it's reserved)
        if (selectedTime) {
          const selectedSlot = slots.find(slot => slot.time === selectedTime);
          if (selectedSlot && selectedSlot.status === 'reserved') {
            onTimeChangeRef.current('');
          }
        }
      } catch (err) {
        console.error('Error fetching slots:', err);
        setFetchError('Error al cargar horarios. Por favor intenta de nuevo.');
        setAllSlots([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllSlots();
  }, [roomId, selectedDate]); // Remove selectedTime from dependencies to avoid infinite loops

  // Update the ref when onTimeChange changes
  useEffect(() => {
    onTimeChangeRef.current = onTimeChange;
  }, [onTimeChange]);

  // Get available slots for display logic
  const availableSlots = allSlots.filter(slot => slot.status === 'active').map(slot => slot.time);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    onDateChange(newDate);
    // Clear selected time when date changes
    onTimeChange('');
  };

  // Removed handleTimeSelect since we now directly call onTimeChange

  return (
    <Container>
      {/* Date Selection */}
      <Section>
        <SectionTitle>Selecciona la fecha</SectionTitle>
        <DateInput
          type="date"
          value={selectedDate}
          onChange={handleDateChange}
          min={getMinDate()}
          $hasError={!!error}
        />
      </Section>

      {/* Time Selection */}
      <Section>
        <SectionTitle>Selecciona el horario</SectionTitle>
        
        {!selectedDate && (
          <NoSlotsMessage>
            Primero selecciona una fecha para ver los horarios disponibles
          </NoSlotsMessage>
        )}
        
        {selectedDate && isLoading && (
          <LoadingMessage>
            Cargando horarios disponibles...
          </LoadingMessage>
        )}
        
        {selectedDate && fetchError && (
          <ErrorMessage>
            {fetchError}
          </ErrorMessage>
        )}
        
        {selectedDate && !isLoading && !fetchError && allSlots.length === 0 && (
          <NoSlotsMessage>
            No hay horarios para esta fecha
          </NoSlotsMessage>
        )}
        
        {selectedDate && !isLoading && !fetchError && allSlots.length > 0 && (
          <TimeSlotGrid>
            {allSlots.map((slot) => {
              const isSelected = selectedTime === slot.time;
              const isDisabled = slot.status === 'reserved';
              
              return (
                <TimeSlotButton
                  key={slot.time}
                  type="button"
                  onClick={() => !isDisabled && onTimeChange(slot.time)}
                  $isSelected={isSelected}
                  $isDisabled={isDisabled}
                  disabled={isDisabled}
                >
                  {formatTimeForDisplay(slot.time)}
                </TimeSlotButton>
              );
            })}
          </TimeSlotGrid>
        )}
      </Section>
      
      {error && (
        <ErrorMessage>
          {error}
        </ErrorMessage>
      )}
    </Container>
  );
};